import React, { useCallback, useEffect, useState } from 'react';
import { BACKLOG_DAYS, clearBacklog, countBacklog, undoBacklog } from '../../utils/conversationBacklog';

/**
 * One press for the whole backlog.
 *
 * The first real sweep put 158 threads on this screen. Not junk — Bloomberg
 * alone has 31 live threads — but a real backlog nobody had ever seen in one
 * place, and 158 individual presses is an evening, so nobody would do it and
 * the tab would go unread.
 *
 * It writes the ordinary Done mark against each thread's own last message, so
 * anything that gets a reply comes straight back. After the first press the
 * screen shows only what has actually moved since, which is the state this tab
 * is worth reading in.
 *
 * It hides itself when there is nothing old enough to clear, so on a normal
 * morning this row does not exist. Nothing to learn and nothing to ignore —
 * see the note in DispatchSearch about not training people to use this screen.
 */
export function ClearBacklog({
  onCleared
}: {
  onCleared: () => void;
}) {
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState<{
    batchId: string;
    marked: number;
  } | null>(null);
  const refresh = useCallback(async () => {
    setCount(await countBacklog());
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  /* Longer than the per-row toast: a hundred rows is a bigger thing to have
     done by accident, and worth a longer moment to notice. */
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(null), 15000);
    return () => window.clearTimeout(timer);
  }, [undo]);
  async function clear() {
    setBusy(true);
    const result = await clearBacklog();
    setBusy(false);
    if (!result || !result.marked) return;
    setUndo({
      batchId: result.batchId,
      marked: result.marked
    });
    await refresh();
    onCleared();
  }
  async function revert() {
    if (!undo) return;
    setBusy(true);
    const ok = await undoBacklog(undo.batchId);
    setBusy(false);
    if (!ok) return;
    setUndo(null);
    await refresh();
    onCleared();
  }
  if (!count && !undo) return null;
  return <>
      {count > 0 && <div className="mt-6 flex flex-col gap-3 border border-ruleDark px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6" data-id="element-797" data-mp="1kpwkby47oiu0">
          <p className="m-0 max-w-[52ch] text-[0.95rem] leading-snug text-paper/60" data-id="element-798" data-mp="1pr3ta0i3bo29">
            <span className="text-paper" data-id="element-799" data-mp="1unu7sje8yblk">{count}</span> of these have not moved in over{' '}
            {BACKLOG_DAYS} days. Clearing them marks them done — any that gets a reply comes back on
            its own.
          </p>
          <button type="button" onClick={() => void clear()} disabled={busy} className="label inline-flex h-10 shrink-0 items-center justify-center border border-paper/30 px-5 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper disabled:opacity-40" data-id="element-800" data-mp="23j0j1xwadlvh">
            {busy ? 'Clearing' : `Clear ${count}`}
          </button>
        </div>}

      {undo && <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center px-6 pb-24 sm:pb-8" data-id="element-801" data-mp="1ri4cpsge1g2">
          <div className="flex max-w-full items-center gap-5 border border-ruleDark bg-ink px-5 py-3" data-id="element-802" data-mp="3sx9lizo963qq">
            <span className="truncate text-[0.95rem] text-paper/80" data-id="element-803" data-mp="3o01bxnh67813">
              {undo.marked} cleared
            </span>
            <button type="button" onClick={() => void revert()} disabled={busy} className="label inline-flex h-10 shrink-0 items-center border border-paper/30 px-4 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper disabled:opacity-40" data-id="element-804" data-mp="pgt2s0mbzjr1">
              Undo
            </button>
          </div>
        </div>}
    </>;
}